<template>
	<v-card class="d-flex flex-column h-100" style="min-height: 300px">
		<v-toolbar density="compact" v-if="!props.hideToolbar">
			<template v-if="$slots.prepend" #prepend>
				<slot name="prepend"></slot>
			</template>

			<v-toolbar-title class="text-body-1">
				{{ apiDocument.file_notes }}
			</v-toolbar-title>
			<v-btn stacked @click.stop="$emit('close')">
				<v-icon icon="far fa-xmark"></v-icon>
			</v-btn>
		</v-toolbar>

		<v-container
			fluid
			class="flex-shrink-0 flex-grow-1 bg-surface-darken-1 pa-2"
			ref="pdfContainer"
		>
			<Loading
				v-if="isLoading"
				text="Datei wird geladen..."
				:progress="totalProgress"
			>
			</Loading>

			<v-row class="h-100" dense>
				<v-col :xl="2" :lg="3" :md="4" v-if="!isMobile" v-show="thumbnails">
					<div class="pdf-pages">
						<div
							ref="thumbnailsContainer"
							@scroll="handleThumbnailScroll"
							class="wrapper"
						>
							<v-card
								v-for="(_, index) in thumbnailPages"
								:key="index"
								class="thumbnail-wrapper"
								:class="{ active: currentPage === index + 1 }"
								:id="`thumbnail-${index + 1}`"
								@click="scrollToPage(index + 1)"
							>
								<div class="thumbnail-canvas-container">
									<canvas
										:ref="(el) => (thumbnailElements[index] = el)"
										class="thumbnail-canvas"
										:style="thumbnailStyles(index)"
										v-show="thumbnailsRendered[index]"
									/>
									<v-progress-circular
										v-show="!thumbnailsRendered[index]"
										indeterminate
										color="primary"
										size="24"
										width="2"
									></v-progress-circular>
								</div>
								<v-chip label density="compact" class="my-2"
									>Seite {{ index + 1 }}</v-chip
								>
							</v-card>
						</div>
					</div>
				</v-col>
				<v-col :cols="pagesContainerCols">
					<div class="pdf-pages">
						<div
							ref="pdfPagesContainer"
							class="wrapper d-flex flex-column align-center"
							@scroll="handleScroll"
						>
							<template v-if="pdfDocument !== null">
								<div
									v-for="(_, index) in pages"
									:key="index"
									:ref="
										(el) =>
											observePageContainer(el as HTMLElement | null, index)
									"
									:style="{
										...pageStyles(index, true),
										'justify-self': 'center',
									}"
									:id="`page-${index + 1}`"
									:data-page-index="index"
									class="page-container"
								>
									<div
										class="page-canvas-container"
										:style="pageStyles(index, true)"
									>
										<canvas
											:ref="(el) => (pageElements[index] = el)"
											class="pdf-page"
											:style="pageStyles(index)"
											v-show="pagesRendered[index]"
										/>
										<div
											v-if="!pagesRendered[index]"
											:style="pageStyles(index, true)"
											class="elevation-1 bg-white d-flex flex-column justify-center align-center"
										>
											<template v-if="pagesFailed[index]">
												<v-icon
													icon="far fa-triangle-exclamation"
													color="warning"
													size="40"
													class="mb-2"
												></v-icon>
												<div class="text-body-2 mb-3">
													Seite {{ index + 1 }} konnte nicht geladen werden.
												</div>
												<v-btn
													size="small"
													variant="tonal"
													color="primary"
													@click="retryPage(index)"
												>
													Erneut versuchen
												</v-btn>
											</template>
											<Loading v-else></Loading>
										</div>
									</div>
								</div>
							</template>

							<div
								v-else
								class="d-flex flex-column justify-center align-center fill-height"
							>
								<BaseImage
									:src="null"
									style="width: 90vw"
									show-file-not-found-text
									error-info="component.baseImage.errorInfo"
								>
								</BaseImage>
							</div>
						</div>
					</div>
				</v-col>
			</v-row>
		</v-container>

		<v-toolbar v-show="!isLoading" class="toolbar-controls flex-shrink-0">
			<v-select
				v-model="currentPage"
				class="page-selector ml-5"
				hide-details
				:items="pages.map((_, i) => i + 1)"
				density="comfortable"
				variant="outlined"
				max-width="150px"
				width="50px"
				@update:model-value="(value) => scrollToPage(value)"
			>
				<template #append-inner> / {{ pages.length }} </template>
			</v-select>

			<v-btn-toggle
				v-model="viewMode"
				class="mx-5 d-none d-sm-flex"
				variant="outlined"
				mandatory
				divided
			>
				<v-btn value="fitWidth" size="small"
					><v-icon>fa-light fa-arrows-left-right</v-icon></v-btn
				>
				<v-btn value="fitPage" size="small"
					><v-icon
						>fa-light fa-up-right-and-down-left-from-center</v-icon
					></v-btn
				>
				<v-btn value="100"><v-icon>fa-light fa-percent</v-icon></v-btn>
			</v-btn-toggle>
			<ButtonWrapper v-if="pdfDocument" :load="true">
				<ButtonIcon
					v-if="!isMobile && display.smAndUp"
					icon="fa-light fa-gallery-thumbnails"
					color="black"
					text="Thumbnails"
					notext
					@click.stop="thumbnails = !thumbnails"
				></ButtonIcon>
				<ButtonIcon
					@click.stop="rotatePage('right')"
					icon="fa-light fa-rotate-right"
					color="black"
					text="Seite drehen"
					notext
				></ButtonIcon>
				<ButtonIcon
					@click.stop="adaptScale(0.1)"
					:disabled="scale >= 4"
					icon="fa-light fa-magnifying-glass-plus"
					color="black"
					text="Vergrößern"
					notext
				>
				</ButtonIcon>
				<ButtonIcon
					@click.stop="adaptScale(-0.1)"
					:disabled="scale <= 0.2"
					color="black"
					icon="fa-light fa-magnifying-glass-minus"
					text="Verkleinern"
					notext
				>
				</ButtonIcon>

				<ButtonIcon
					@click.stop="save"
					:disabled="isLoading"
					color="black"
					icon="fa-light fa-floppy-disk"
					text="Speichern"
					notext
				>
				</ButtonIcon>
				<ButtonIcon
					@click.stop="print"
					:disabled="isLoading"
					color="black"
					icon="fa-light fa-print"
					text="Drucken"
					notext
				>
				</ButtonIcon>
			</ButtonWrapper>
		</v-toolbar>
	</v-card>
</template>

<script lang="ts" setup>
import BaseImage from '@/components/templates/BaseImage.vue'
import { useDocumentStore } from '@/stores/modules/document'
import { useElementSize } from '@vueuse/core'
import useFileHandler from 'composables/useFileHandler.ts'
import type {
	PDFDocumentLoadingTask,
	PDFDocumentProxy,
	PDFPageProxy,
} from 'pdfjs-dist'
import { getDocument } from 'pdfjs-dist'
import 'pdfjs-dist/build/pdf.worker.mjs'
import ButtonIcon from 'templates/Base/ButtonIcon.vue'
import ButtonWrapper from 'templates/Base/ButtonWrapper.vue'
import Loading from 'templates/Loading.vue'
import {
	computed,
	nextTick,
	onBeforeUnmount,
	onMounted,
	ref,
	useTemplateRef,
	watch,
} from 'vue'
import { useDisplay } from 'vuetify'
import { useGesture } from '@vueuse/gesture'

const props = defineProps({
	id: {
		type: Number,
		required: false,
		default: undefined,
	},
	blob: {
		default: undefined,
		type: Blob,
		required: false,
	},
	heightFactor: {
		type: Number,
		default: 1,
	},
	showThumbnails: {
		type: Boolean,
		default: true,
	},
	pageWidthPercentage: {
		type: Number,
		default: 100,
	},
	hideToolbar: {
		type: Boolean,
		default: false,
	},
})

type PageRange = {
	start: number
	end: number
}

const emit = defineEmits(['page-change', 'close', 'loading'])

const documentStore = useDocumentStore()
const display = useDisplay()
const { name: breakpointName, width: windowWidth } = display
const { printPdf, downloadBlob } = useFileHandler()

const isMobile = computed(() => display.mobile.value)

watch(isMobile, (newVal) => {
	if (newVal === true && thumbnails.value) {
		thumbnails.value = false
		calculateScale()
	}
})

const RENDER_SCALE = computed(() => {
	const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1
	const desired = isMobile.value ? dpr : dpr * 1.5
	return Math.min(Math.max(desired, 1.5), 3)
})
const THUMBNAIL_SCALE = 0.5

const pages = ref<number[]>([])
const thumbnailPages = ref<number[]>([])
const pageElements = ref<(HTMLCanvasElement | null)[]>([])
const pageContainerElements = ref<(HTMLElement | null)[]>([])
let pageObserver: IntersectionObserver | null = null
const pageTransforms = ref<
	{ rotate: number; width: number | undefined; height: number | undefined }[]
>([])
const visiblePageRange = ref<PageRange>({ start: 0, end: 2 })
const renderBuffer = 2 // Pages to render before/after visible
const thumbnailElements = ref<(HTMLCanvasElement | null)[]>([])
const pagesRendered = ref<boolean[]>([])
const pagesFailed = ref<boolean[]>([])
const thumbnailsRendered = ref<boolean[]>([])
const currentPage = ref(1)
const pdfPagesContainer = ref<any>(null)
const pdfContainer = ref<any>(null)
const thumbnailsContainer = ref<any>(null)
const isScrolling = ref(false)
const isThumbnailScrolling = ref(false)
const isLoading = ref(true)
let pdfDocument: PDFDocumentProxy | undefined | null = undefined
// Tracked so we can cancel the in-flight binary download (axios) and PDF
// parsing (pdfjs) when the dialog closes mid-load. Without these, closing
// before "Datei wird geladen…" finishes leaves the request and the worker
// running until they complete on their own.
let fetchAbortController: AbortController | null = null
let pdfLoadingTask: PDFDocumentLoadingTask | null = null
let isUnmounting = false
const binary = ref<Blob>()
const thumbnails = ref<boolean>(
	!display.mobile.value && props.showThumbnails && !display.smAndDown.value
)
const pageDimensions = ref<{ width: number; height: number }[]>([])
const viewMode = defineModel<string>('viewMode', { default: 'fitPage' })
const activeRenderTasks = ref<Map<number, any>>(new Map())
const renderQueue = ref<Set<number>>(new Set())
const apiDocument = computed(() => {
	return documentStore.getItem(props.id as number) as Api.Document
})
const pagesContainerCols = computed(() => {
	if (!thumbnails.value) {
		return 12
	}
	switch (breakpointName.value) {
		case 'xs':
			return 12
		case 'sm':
			return 12
		case 'md':
			return 8
		case 'lg':
			return 9
		case 'xl':
			return 10
		case 'xxl':
			return 10
		default:
			return 12
	}
})
const scale = ref<number>(0.9)
// Phase weights for the unified loading bar:
// - download covers 0–80% (the slowest part, especially on large files)
// - render of priority pages covers 80–100%
// totalProgress is always a number so Loading never shows the indeterminate
// spinner (the user sees the percentage immediately).
const downloadProgress = ref<number>(0)
const totalProgress = computed(() => {
	const downloadShare = Math.min(downloadProgress.value, 100) * 0.8
	if (pages.value.length === 0) {
		return Math.round(downloadShare)
	}
	const renderedCount = pagesRendered.value.filter((r) => r === true).length
	const renderShare = (renderedCount / pages.value.length) * 20
	return Math.round(downloadShare + renderShare)
})

const pageStyles = (index: number, isContainer: boolean = false) => {
	const dim = pageDimensions.value[index]
	if (!dim) return {}

	const displayWidth = dim.width * scale.value * RENDER_SCALE.value
	const displayHeight = dim.height * scale.value * RENDER_SCALE.value

	const values: Record<string, string | number> = {
		width: `${displayWidth}px`,
		height: `${displayHeight}px`,
	}

	if (isContainer) {
		return values
	}

	// Apply rotation transform for the canvas itself
	if (pageTransforms.value[index]?.rotate) {
		values.transform = `rotate(${pageTransforms.value[index].rotate}deg)`
	}

	return values
}

const thumbnailStyles = (index: number) => {
	const dim = pageDimensions.value[index]
	if (!dim) return {}

	const thumbWidth = dim.width * THUMBNAIL_SCALE
	const thumbHeight = dim.height * THUMBNAIL_SCALE

	return {
		width: `${thumbWidth}px`,
		height: `${thumbHeight}px`,
		maxWidth: '100%',
		objectFit: 'contain',
	}
}

const adaptScale = async (value: number) => {
	if (viewMode.value !== '100') {
		viewMode.value = '100'
	}
	scale.value += value
	// Force re-render of visible pages at new scale
	if (!pdfDocument) {
		return
	}
	const { start, end } = visiblePageRange.value
	await renderVisiblePageRange(visiblePageRange.value)
	await scrollToPage(currentPage.value)
}

const rotatePage = (direction: 'left' | 'right') => {
	let value = pageTransforms.value[currentPage.value - 1].rotate
	value += direction === 'left' ? -45 : 45
	if (value > 360) {
		value = 0
	}
	pageTransforms.value[currentPage.value - 1].rotate = value
}

const fetchPDF = async () => {
	// Cancel any previous in-flight fetch (prop change racing with mount, etc.)
	if (fetchAbortController) fetchAbortController.abort()
	fetchAbortController = new AbortController()
	const signal = fetchAbortController.signal

	try {
		isLoading.value = true
		downloadProgress.value = 0

		if (typeof props.blob === 'undefined' && typeof props.id === 'undefined') {
			console.error('No ID or Blob provided')
			isLoading.value = false
			return
		}

		let data: Blob
		if (typeof props.blob === 'undefined') {
			const response = await documentStore.documentBinary({
				id: props.id,
				signal,
				onDownloadProgress: (e) => {
					if (e.total) {
						downloadProgress.value = (e.loaded / e.total) * 100
					} else if (typeof e.loaded === 'number') {
						// Server didn't send Content-Length (e.g. gzip); creep up
						// asymptotically toward 95% so the bar still moves visibly.
						const pseudo = 1 - 1 / (1 + e.loaded / 524288)
						downloadProgress.value = Math.min(95, pseudo * 95)
					}
				},
			})
			downloadProgress.value = 100
			data = response.data
		} else {
			downloadProgress.value = 100
			data = props.blob
		}

		if (signal.aborted || isUnmounting) return

		binary.value = data
		await loadPDF(data)
	} catch (error: any) {
		// Closed before the binary finished — expected, no error UI needed.
		if (error?.name === 'CanceledError' || error?.name === 'AbortError') {
			return
		}
		pdfDocument = null
		console.error('Error fetching PDF:', error)
	} finally {
		isLoading.value = false
	}
}

// pdf.js 5.x loads its image decoders (OpenJPEG/JPX, JBIG2), CMaps and standard
// fonts from separate assets. Without these URLs, scanned PDFs (JPEG2000/JBIG2
// images) render as a blank page. The assets are copied to /pdfjs/* by
// vite-plugin-static-copy (see vite.config.mts); base-relative so dev and prod
// (which may be served under a sub-path) both resolve correctly. BASE_URL is
// normalised by Vite to always end in '/', so the join needs no separator.
const PDFJS_ASSET_BASE = `${import.meta.env.BASE_URL}pdfjs/`

const loadPDF = async (pdfBlob: Blob) => {
	try {
		const pdfBinary = await pdfBlob.arrayBuffer()
		if (isUnmounting) return
		pdfLoadingTask = getDocument({
			data: pdfBinary,
			wasmUrl: `${PDFJS_ASSET_BASE}wasm/`,
			cMapUrl: `${PDFJS_ASSET_BASE}cmaps/`,
			cMapPacked: true,
			standardFontDataUrl: `${PDFJS_ASSET_BASE}standard_fonts/`,
		})
		const pdf = await pdfLoadingTask.promise

		if (isUnmounting) {
			try {
				pdf.destroy()
			} catch (_) {
				// ignore
			}
			return
		}

		pdfDocument = pdf

		const pageCount = pdf.numPages
		pages.value = Array.from({ length: pageCount }, (_, i) => i + 1)
		thumbnailPages.value = [...pages.value]

		pageTransforms.value = Array.from({ length: pageCount }, () => ({
			rotate: 0,
			width: undefined,
			height: undefined,
		}))
		pageElements.value = Array(pageCount).fill(null)
		thumbnailElements.value = Array(pageCount).fill(null)
		pageVisibility.clear()
		pagesRendered.value = Array(pageCount).fill(false)
		pagesFailed.value = Array(pageCount).fill(false)
		thumbnailsRendered.value = Array(pageCount).fill(false)

		await calculateAllPageDimensions(pdf)
		await nextTick()
		await calculateScale()
		await renderPages(pdf)
	} catch (error) {
		console.error('Error loading PDF:', error)
	}
}

const calculateAllPageDimensions = async (pdf: PDFDocumentProxy) => {
	const dimensionsPromises = pages.value.map(async (pageNum) => {
		const page = await pdf.getPage(pageNum)
		const baseViewport = page.getViewport({ scale: 1 })
		return {
			width: baseViewport.width,
			height: baseViewport.height,
		}
	})

	pageDimensions.value = await Promise.all(dimensionsPromises)
}

const pageVisibility = new Map<number, number>()

const setupPageObserver = () => {
	if (pageObserver) return
	if (
		typeof window === 'undefined' ||
		typeof IntersectionObserver === 'undefined'
	) {
		return
	}

	// root: null → use the viewport. This works regardless of which ancestor
	// is the actual scroll container (BaseDialog, the .wrapper, etc.).
	pageObserver = new IntersectionObserver(
		(entries) => {
			for (const entry of entries) {
				const el = entry.target as HTMLElement
				const idx = Number(el.dataset.pageIndex)
				if (Number.isNaN(idx)) continue

				if (entry.isIntersecting) {
					pageVisibility.set(idx, entry.intersectionRatio)
					if (pdfDocument && !pagesRendered.value[idx]) {
						renderPageWithIndex(pdfDocument, idx)
					}
				} else {
					pageVisibility.delete(idx)
				}
			}

			if (isScrolling.value) return

			// pick the most-visible page and sync currentPage + thumbnail rail
			let bestPage = currentPage.value
			let bestRatio = 0
			for (const [idx, ratio] of pageVisibility) {
				if (ratio > bestRatio) {
					bestRatio = ratio
					bestPage = idx + 1
				}
			}
			if (bestRatio > 0 && bestPage !== currentPage.value) {
				currentPage.value = bestPage
				emit('page-change', bestPage)
				if (thumbnails.value && !isThumbnailScrolling.value) {
					scrollThumbnailIntoView(bestPage)
				}
			}
		},
		{
			root: null,
			rootMargin: '600px 0px',
			threshold: [0, 0.1, 0.25, 0.5, 0.75, 1.0],
		}
	)

	// observe anything already mounted (subsequent mounts go through observePageContainer)
	pageContainerElements.value.forEach((el) => {
		if (el) pageObserver!.observe(el)
	})
}

const observePageContainer = (el: HTMLElement | null, index: number) => {
	const prev = pageContainerElements.value[index]
	if (prev && prev !== el && pageObserver) {
		pageObserver.unobserve(prev)
	}
	pageContainerElements.value[index] = el
	if (el && pageObserver) {
		pageObserver.observe(el)
	}
}

const renderPages = async (pdf: PDFDocumentProxy) => {
	const visiblePageIndex = currentPage.value - 1
	const priorityIndexes = [visiblePageIndex]

	// Add pages before and after current page
	for (let i = 1; i <= 2; i++) {
		if (visiblePageIndex - i >= 0) {
			priorityIndexes.push(visiblePageIndex - i)
		}
		if (visiblePageIndex + i < pages.value.length) {
			priorityIndexes.push(visiblePageIndex + i)
		}
	}

	// Render priority pages first — we await these so the loading overlay
	// stays up until the user has something to look at.
	await Promise.all(
		priorityIndexes.map((index) => renderPageWithIndex(pdf, index))
	)

	// Render the rest in the background. NOT awaited: the loading overlay
	// should disappear as soon as priority pages are ready; the rest streams
	// in while the user reads page 1. Renders in bounded-concurrency batches
	// so pages fill in fast (pdfjs handles its own internal throttling)
	// without a per-page sleep that left big PDFs hanging unrendered when
	// the user scrolled away from the priority window.
	const remainingIndexes = pages.value
		.map((_, i) => i)
		.filter((i) => !priorityIndexes.includes(i))

	const concurrency = 4
	void (async () => {
		for (let i = 0; i < remainingIndexes.length; i += concurrency) {
			if (pdfDocument !== pdf) return // new PDF was loaded, abort
			const batch = remainingIndexes.slice(i, i + concurrency)
			await Promise.all(batch.map((idx) => renderPageWithIndex(pdf, idx)))
		}
	})()
}

const renderPageWithIndex = async (pdf: PDFDocumentProxy, index: number) => {
	// Only render if not already rendered
	if (pagesRendered.value[index]) {
		return
	}

	// Check if already queued or rendering
	if (renderQueue.value.has(index)) {
		return
	}

	// Cancel any existing render task for this page
	const existingTask = activeRenderTasks.value.get(index)
	if (existingTask) {
		try {
			existingTask.cancel()
		} catch (e) {
			// Ignore cancellation errors
		}
		activeRenderTasks.value.delete(index)
	}

	renderQueue.value.add(index)
	pagesFailed.value[index] = false

	try {
		const page = await pdf.getPage(index + 1)

		const pageElement = pageElements.value[index]
		const thumbnailElement = thumbnailElements.value[index]

		if (pageElement) {
			await renderPage(page, pageElement, RENDER_SCALE.value, index)
			pagesRendered.value[index] = true
			if (
				thumbnailElement &&
				thumbnails.value &&
				!thumbnailsRendered.value[index]
			) {
				renderThumbnailFromPage(pageElement, thumbnailElement)
				thumbnailsRendered.value[index] = true
			}
		}
	} catch (error: any) {
		// Ignore cancellation errors
		if (error.name !== 'RenderingCancelledException') {
			console.error(`Error rendering page ${index + 1}:`, error)
			pagesFailed.value[index] = true
		}
	} finally {
		renderQueue.value.delete(index)
		activeRenderTasks.value.delete(index)
	}
}

const retryPage = (index: number) => {
	if (!pdfDocument) return
	renderPageWithIndex(pdfDocument, index)
}

const renderPage = async (
	page: PDFPageProxy,
	canvas: HTMLCanvasElement,
	renderScale: number = RENDER_SCALE.value,
	pageIndex?: number
) => {
	const viewport = page.getViewport({ scale: renderScale })
	const context = canvas.getContext('2d')

	if (!context) {
		console.error('Unable to get canvas context')
		return
	}

	canvas.width = viewport.width
	canvas.height = viewport.height

	try {
		const renderTask = page.render({
			canvasContext: context,
			viewport,
		})

		// Store the render task if we have a page index
		if (typeof pageIndex !== 'undefined') {
			activeRenderTasks.value.set(pageIndex, renderTask)
		}

		await renderTask.promise

		return { width: viewport.width, height: viewport.height }
	} catch (error: any) {
		// Re-throw if it's not a cancellation
		if (error.name !== 'RenderingCancelledException') {
			console.error('Error rendering page:', error)
			throw error
		}
	}
}

const renderVisibleThumbnails = async (pdf: PDFDocumentProxy) => {
	const thumbnailsContainerEl = safelyGetElement(thumbnailsContainer.value)
	if (!thumbnailsContainerEl) return

	const containerRect = thumbnailsContainerEl.getBoundingClientRect()

	// Get all thumbnail wrappers
	const thumbnailWrappers = Array.from(
		thumbnailsContainerEl.querySelectorAll('.thumbnail-wrapper')
	) as HTMLElement[]

	const visibleIndices: number[] = []
	const bufferZone = 200 // pixels above/below viewport

	thumbnailWrappers.forEach((wrapper, index) => {
		const rect = wrapper.getBoundingClientRect()
		const isVisible =
			rect.bottom > containerRect.top - bufferZone &&
			rect.top < containerRect.bottom + bufferZone

		if (isVisible && !thumbnailsRendered.value[index]) {
			visibleIndices.push(index)
		}
	})

	// Render visible thumbnails
	for (const index of visibleIndices) {
		await renderThumbnail(pdf, index)
	}
}

const renderThumbnail = async (pdf: PDFDocumentProxy, index: number) => {
	if (thumbnailsRendered.value[index]) {
		return // Already rendered
	}

	try {
		const page = await pdf.getPage(index + 1)
		const thumbnailElement = thumbnailElements.value[index]

		if (!thumbnailElement) {
			console.warn(`Thumbnail element for page ${index + 1} not found`)
			return
		}

		// Get the base viewport
		const baseViewport = page.getViewport({ scale: 1 })

		// Calculate thumbnail dimensions
		const thumbWidth = baseViewport.width * THUMBNAIL_SCALE
		const thumbHeight = baseViewport.height * THUMBNAIL_SCALE

		// Set canvas dimensions
		thumbnailElement.width = thumbWidth
		thumbnailElement.height = thumbHeight

		// Get canvas context
		const context = thumbnailElement.getContext('2d')
		if (!context) {
			console.error('Unable to get thumbnail canvas context')
			return
		}

		// Create viewport at thumbnail scale
		const thumbnailViewport = page.getViewport({ scale: THUMBNAIL_SCALE })

		// Render the page to thumbnail canvas
		await page.render({
			canvasContext: context,
			viewport: thumbnailViewport,
		}).promise

		// Mark as rendered
		thumbnailsRendered.value[index] = true
	} catch (error: any) {
		if (error.name !== 'RenderingCancelledException') {
			console.error(`Error rendering thumbnail ${index + 1}:`, error)
		}
	}
}

const renderThumbnailFromPage = (
	pageCanvas: HTMLCanvasElement,
	thumbnailCanvas: HTMLCanvasElement
) => {
	if (!pageCanvas || !thumbnailCanvas) return

	const pageCtx = pageCanvas.getContext('2d')
	const thumbCtx = thumbnailCanvas.getContext('2d')

	if (!pageCtx || !thumbCtx) return

	// Calculate thumbnail dimensions based on page canvas
	const thumbWidth = (pageCanvas.width * THUMBNAIL_SCALE) / RENDER_SCALE.value
	const thumbHeight = (pageCanvas.height * THUMBNAIL_SCALE) / RENDER_SCALE.value

	// Set thumbnail canvas size
	thumbnailCanvas.width = thumbWidth
	thumbnailCanvas.height = thumbHeight

	// Draw the page canvas to thumbnail canvas with scaling
	thumbCtx.drawImage(
		pageCanvas,
		0,
		0,
		pageCanvas.width,
		pageCanvas.height, // Source dimensions
		0,
		0,
		thumbWidth,
		thumbHeight // Destination dimensions
	)
}

const safelyGetElement = (el: any): HTMLElement | null => {
	if (!el) return null
	return el.$el || el
}

const handleThumbnailScroll = () => {
	if (!pdfDocument) {
		return
	}
	renderVisibleThumbnails(pdfDocument)
}

const handleScroll = () => {
	const container = safelyGetElement(pdfPagesContainer.value)
	if (!container) return

	const containerHeight = container.clientHeight

	// Get all page elements
	const pageContainers = Array.from(
		container.querySelectorAll('.page-container')
	) as HTMLElement[]

	if (pageContainers.length === 0) return

	let bestVisiblePage = 1
	let bestVisibility = 0
	let firstVisible = -1
	let lastVisible = -1

	pageContainers.forEach((pageContainer, index) => {
		const rect = pageContainer.getBoundingClientRect()
		const containerRect = container.getBoundingClientRect()

		const top = rect.top - containerRect.top
		const bottom = rect.bottom - containerRect.top

		// Check if page is visible
		const isVisible = top < containerHeight && bottom > 0

		if (isVisible) {
			if (firstVisible === -1) firstVisible = index
			lastVisible = index

			// Calculate how much of the page is visible in the viewport
			const pageTop = Math.max(0, top)
			const pageBottom = Math.min(containerHeight, bottom)
			const visibleHeight = Math.max(0, pageBottom - pageTop)
			const percentVisible = visibleHeight / rect.height

			// Also consider how close the page is to the center of the viewport
			const distanceToCenter = Math.abs(
				(rect.top + rect.bottom) / 2 -
					(containerRect.top + containerRect.bottom) / 2
			)
			const centerFactor = 1 - Math.min(1, distanceToCenter / containerHeight)

			const combinedVisibility = percentVisible * 0.7 + centerFactor * 0.3

			if (combinedVisibility > bestVisibility) {
				bestVisibility = combinedVisibility
				bestVisiblePage = index + 1
			}
		}
	})

	// Update current page
	if (bestVisiblePage !== currentPage.value) {
		currentPage.value = bestVisiblePage
		emit('page-change', currentPage.value)

		// Auto-scroll the thumbnail into view
		if (thumbnails.value && !isThumbnailScrolling.value) {
			scrollThumbnailIntoView(bestVisiblePage)
		}
	}

	// Update visible range for rendering
	if (firstVisible !== -1 && lastVisible !== -1) {
		const start = Math.max(0, firstVisible - renderBuffer)
		const end = Math.min(pages.value.length - 1, lastVisible + renderBuffer)

		// Only update if range changed
		if (
			visiblePageRange.value.start !== start ||
			visiblePageRange.value.end !== end
		) {
			visiblePageRange.value = { start, end }
			renderVisiblePageRange({ start, end })
		}
	}
}

const scrollThumbnailIntoView = (pageNumber: number) => {
	const thumbnailEl = document.getElementById(`thumbnail-${pageNumber}`)
	const thumbnailsContainerEl = safelyGetElement(thumbnailsContainer.value)

	if (!thumbnailEl || !thumbnailsContainerEl) return

	isThumbnailScrolling.value = true

	// Calculate the scroll position to center the thumbnail
	const thumbnailRect = thumbnailEl.getBoundingClientRect()
	const containerRect = thumbnailsContainerEl.getBoundingClientRect()

	// Calculate the offset to center the thumbnail in the container
	const thumbnailCenter = thumbnailEl.offsetTop + thumbnailRect.height / 2
	const containerCenter = containerRect.height / 2
	const scrollTarget = thumbnailCenter - containerCenter

	// Smooth scroll the thumbnails container
	thumbnailsContainerEl.scrollTop = Math.max(
		0,
		Math.min(
			scrollTarget,
			thumbnailsContainerEl.scrollHeight - thumbnailsContainerEl.clientHeight
		)
	)

	setTimeout(() => {
		isThumbnailScrolling.value = false
	}, 300)
}

let scrollEndTimer: ReturnType<typeof setTimeout> | null = null
let scrollEndCleanup: (() => void) | null = null

const releaseScrollLock = () => {
	if (scrollEndTimer) {
		clearTimeout(scrollEndTimer)
		scrollEndTimer = null
	}
	if (scrollEndCleanup) {
		scrollEndCleanup()
		scrollEndCleanup = null
	}
	isScrolling.value = false
}

const scrollToPage = (pageNumber: number) => {
	// Update active state IMMEDIATELY so the highlight follows the click,
	// independently of whether render/scroll succeed.
	isScrolling.value = true
	currentPage.value = pageNumber
	emit('page-change', currentPage.value)
	scrollThumbnailIntoView(pageNumber)

	// Make sure the target page is queued for render (no-op if already rendered).
	const start = Math.max(0, pageNumber - renderBuffer)
	const end = Math.min(pages.value.length - 1, pageNumber + renderBuffer)
	visiblePageRange.value = { start, end }
	renderVisiblePageRange({ start, end })

	// scrollIntoView walks up to the nearest scrollable ancestor, so it works
	// no matter whether .wrapper or some outer dialog div actually scrolls.
	const targetElement = document.getElementById(`page-${pageNumber}`)
	if (targetElement) {
		targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
	}

	// Hold the scroll lock until the smooth animation actually finishes,
	// otherwise the IO callback resumes mid-animation and drags the thumbnail
	// rail along through the in-between pages. Prefer the native scrollend
	// event (Chrome 114+, Firefox 109+, Safari 17.4+); fall back to a 1500ms
	// timeout for older browsers. Any chained click resets the lock.
	if (scrollEndTimer) clearTimeout(scrollEndTimer)
	if (scrollEndCleanup) scrollEndCleanup()

	if (typeof window !== 'undefined' && 'onscrollend' in window) {
		const onScrollEnd = () => releaseScrollLock()
		window.addEventListener('scrollend', onScrollEnd, {
			once: true,
			capture: true,
		})
		scrollEndCleanup = () =>
			window.removeEventListener('scrollend', onScrollEnd, { capture: true })
	}
	// Always arm the timeout as a safety net — scrollend doesn't fire if the
	// element was already at the target position.
	scrollEndTimer = setTimeout(releaseScrollLock, 1500)
}

const pagesContainer = useTemplateRef('pdfPagesContainer')
const pagesContainerSizes = useElementSize(pagesContainer)

const calculateScale = async () => {
	if (!pdfDocument || !pdfContainer.value) return

	if (!pagesContainerSizes.height.value && !pagesContainerSizes.width.value)
		return
	const firstPage = await pdfDocument.getPage(1)

	// Get viewport at scale 1 (actual PDF dimensions)
	const baseViewport = firstPage.getViewport({ scale: 1 })
	let targetScale = 1

	if (viewMode.value === 'fitWidth') {
		targetScale = pagesContainerSizes.width.value / baseViewport.width
		scale.value = targetScale / RENDER_SCALE.value
	} else if (viewMode.value === 'fitPage') {
		const widthScale = pagesContainerSizes.width.value / baseViewport.width
		const heightScale =
			(pagesContainerSizes.height.value - 24) / baseViewport.height
		targetScale = Math.min(widthScale, heightScale)
		scale.value = targetScale / RENDER_SCALE.value
	}
}

watch(pagesContainerSizes.width, () => {
	calculateScale()
})
watch(pagesContainerSizes.height, () => {
	if (viewMode.value === 'fitPage') {
		calculateScale()
	}
})

const isDragging = ref(false)
const startScroll = { x: 0, y: 0 }

useGesture(
	{
		onPinch: ({ event, movement: [distance] }) => {
			event.preventDefault()

			const zoomingIn = distance > 0
			const zoomingOut = distance < 0

			if (zoomingIn && scale.value <= 4) adaptScale(0.01)
			if (zoomingOut && scale.value >= 0.2) adaptScale(-0.01)
		},

		onDragStart: ({ event }) => {
			const el = pagesContainer.value
			if (!el) return

			// Skip drag-to-scroll for MOUSE pointer entirely. The previous
			// "is the click on the scrollbar gutter?" check failed on macOS
			// overlay scrollbars (offsetWidth - clientWidth === 0), so the
			// scrollbar drag still got hijacked. Mouse users have wheel +
			// native scrollbar; drag-to-scroll is only useful for touch/pen
			// on mobile/tablet.
			const pointer = event as PointerEvent
			if (pointer.pointerType === 'mouse') {
				return
			}

			event.preventDefault()
			isDragging.value = true
			startScroll.x = el.scrollLeft
			startScroll.y = el.scrollTop

			el.style.cursor = 'grabbing'
		},

		onDrag: ({ event, movement: [mx, my] }) => {
			const el = pagesContainer.value
			// Only handle content drags we accepted in onDragStart. Scrollbar
			// drags never set isDragging, so they fall through to native.
			if (!el || !isDragging.value) return

			event.preventDefault()
			el.scrollLeft = startScroll.x - mx
			el.scrollTop = startScroll.y - my
		},

		onDragEnd: ({ event }) => {
			const el = pagesContainer.value
			if (!el) return
			if (!isDragging.value) return

			event.preventDefault()
			isDragging.value = false
			el.style.cursor = ''
		},
	},
	{
		domTarget: pagesContainer,
		eventOptions: { passive: false },
	}
)

const unrenderPage = (index: number) => {
	const canvas = pageElements.value[index]
	if (canvas) {
		const ctx = canvas.getContext('2d')
		if (ctx) {
			ctx.clearRect(0, 0, canvas.width, canvas.height)
			canvas.width = 0
			canvas.height = 0
		}
	}
	pagesRendered.value[index] = false
}

const print = async () => {
	printPdf(binary.value)
}

const save = async () => {
	if (!apiDocument.value || !binary.value) {
		console.error('No document or binary data available for download')
		return
	}
	downloadBlob(
		binary.value,
		documentStore.documentName({
			id: apiDocument.value.id,
			file: apiDocument.value,
		})
	)
}

const renderVisiblePageRange = async (range: PageRange) => {
	if (!pdfDocument) return

	// Render any unrendered pages in the visible range. We no longer unrender
	// off-screen pages: with the reduced RENDER_SCALE memory pressure is low,
	// and unrendering races with the eager background loop in renderPages().
	await Promise.all(
		pagesRendered.value.map(async (_, i) => {
			if (i >= range.start && i <= range.end && !pagesRendered.value[i]) {
				await renderPageWithIndex(pdfDocument!, i)
			}
		})
	)
}

onMounted(async () => {
	// thumbnails initial value is set at the ref declaration so the watcher
	// doesn't fire on mount with pdfDocument still undefined.
	// documentStore.show({ id }) is NOT called here — DialogPreview already
	// loaded the document metadata in onBeforeMount (PdfViewer is gated on
	// file being truthy), so the store cache is populated and apiDocument
	// reads from it.
	await nextTick()
	setupPageObserver()
	await fetchPDF()
})
onBeforeUnmount(() => {
	isUnmounting = true

	if (pageObserver) {
		pageObserver.disconnect()
		pageObserver = null
	}
	if (scrollEndTimer) {
		clearTimeout(scrollEndTimer)
		scrollEndTimer = null
	}
	if (scrollEndCleanup) {
		scrollEndCleanup()
		scrollEndCleanup = null
	}

	// Abort the in-flight binary download (axios cancels the XHR).
	if (fetchAbortController) {
		fetchAbortController.abort()
		fetchAbortController = null
	}

	// Tear down the pdfjs loading task — stops the worker mid-parse if it's
	// still chewing through the file.
	if (pdfLoadingTask) {
		try {
			pdfLoadingTask.destroy()
		} catch (_) {
			// ignore
		}
		pdfLoadingTask = null
	}

	// Cancel any page renders that are currently mid-draw on the worker.
	for (const [, task] of activeRenderTasks.value) {
		try {
			task.cancel()
		} catch (_) {
			// ignore
		}
	}
	activeRenderTasks.value.clear()
	renderQueue.value.clear()

	// Destroy the document last — the background-render IIFE checks
	// pdfDocument identity and bails on its next iteration.
	if (pdfDocument) {
		try {
			pdfDocument.destroy()
		} catch (_) {
			// ignore
		}
		pdfDocument = undefined
	}
})
watch(() => props.id, fetchPDF)
watch(() => props.blob, fetchPDF)
// IMPORTANT: do NOT call scrollToPage() from these watchers. windowWidth /
// breakpointName / useElementSize fire during ordinary scrolling on mobile
// (address-bar collapse, scrollbar appearance, etc.). currentPage usually
// lags one IO tick behind the actual visible page, so scrollToPage() would
// snap the viewport back to a stale position mid-scroll — making it look
// like the scroll position resets and like background pages never render
// (the user just can't reach them). The IO observer keeps currentPage in
// sync with what's actually visible; no manual scrolling needed here.
watch(
	() => windowWidth.value,
	async () => {
		await calculateScale()
		await renderVisiblePageRange(visiblePageRange.value)
	}
)
watch(
	() => breakpointName.value,
	async (value) => {
		if (value === 'sm' || value === 'xs') {
			thumbnails.value = false
		}
		await calculateScale()
		await renderVisiblePageRange(visiblePageRange.value)
	}
)

watch(viewMode, async () => {
	await calculateScale()
	await renderVisiblePageRange(visiblePageRange.value)
})

watch(
	() => thumbnails.value,
	async () => {
		if (!pdfDocument) return
		await renderVisibleThumbnails(pdfDocument)
	}
)
</script>

<style lang="scss">
.toolbar-controls {
	.v-toolbar__content {
		justify-content: center;
	}
}
.page-selector {
	.v-input__control {
		grid-area: auto;
	}
	.v-field__append-inner {
		white-space: nowrap;
	}
}
</style>

<style lang="scss" scoped>
.thumbnail-wrapper {
	cursor: pointer;
	margin-bottom: 8px;
	padding: 5px;
	border: 2px solid transparent;
	display: flex;
	justify-content: center;
	align-items: center;
	transition: border-color 0.2s ease;
	flex-direction: column;
}

.thumbnail-wrapper.active {
	border-color: rgb(var(--v-theme-primary));
	background-color: rgba(var(--v-theme-primary), 0.05);
}

.thumbnail-canvas-container {
	width: 100%;
	display: flex;
	justify-content: center;
	align-items: center;
	min-height: 80px;
}

.thumbnail-canvas {
	max-width: 100%;
	height: auto;
}

/* PDF Viewer */
.pdf-pages {
	height: 100%;
	width: 100%;
	position: relative;
	overflow: hidden;
}
.wrapper {
	position: absolute;
	height: 100%;
	width: 100%;
	overflow: auto;
}

.page-container {
	display: flex;
	flex-direction: column;
	margin-bottom: 20px;
	max-width: 100%; /* Prevent overflow */
}

.page-canvas-container {
	position: relative;
	display: flex;
	justify-content: center;
	align-items: center;
	min-height: 200px;
	width: 100%;
	margin: 0 auto;
	//max-width: 100%; /* Prevent overflow */
	//overflow: hidden; /* Clip content that exceeds bounds */
}

.pdf-page {
	display: block;
	box-shadow: 0 2px 12px rgba(0, 0, 0, 0.2);
	height: auto; /* Maintain aspect ratio */
}
</style>
